import { useState, useEffect } from 'react';

import {
  SIZE_SCALE,
  COLOR_SCALE,
  POSITION_SCALE,
  Z_INDEX,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
/**
 * Scroll progress indicator - shows reading progress at top of page
 */
function ScrollProgress() {
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = (scrollTop / docHeight) * 100;
      setProgress(scrollProgress);
    };

    // Initial calculation
    updateProgress();

    // Update on scroll with throttling
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        updateProgress();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateProgress);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <div
      className={`fixed ${POSITION_SCALE.top['0']} ${POSITION_SCALE.left['0']} right-0 ${SIZE_SCALE['1']} ${COLOR_SCALE['surface-muted']}`}
      style={{ zIndex: Z_INDEX.overlay }}
    >
      <div
        className={`h-full ${COLOR_SCALE.primary} transition-all duration-150 ease-out`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

const ScrollProgressWithMemoryLeakMonitor = withMemoryLeakMonitor(ScrollProgress, {
  componentName: 'ScrollProgress',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 10,
  frequencyThreshold: 2,
});

export default ScrollProgressWithMemoryLeakMonitor;
